import { useCallback, useEffect, useMemo, useState } from 'react'

import { DateTime } from 'luxon'

import { PERIOD_OPTIONS } from '@/components/charts/history/periodOptions'
import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import { useApi, useDashboard } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceWireParams } from '@/insights/common/dataSourceParam'
import { buildQuery } from '@/insights/common/queryUtils'
import { getUserTimezone } from '@/insights/common/timezone'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import {
  type ApiResponse,
  type ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { downloadCsv } from '@/utils/csvExport'

import { SAMPLE_LIMIT } from './constants'
import ReeferSpeedChart from './ReeferSpeedChart'
import type { ReeferSpeedGraphParams, ReeferSpeedSample } from './types'
import { resolveWindow, toWallClock } from './window'

const PATH = '/insights/speed/reefer-speed-by-asset'

const DateOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 24 hours', value: 'last24Hours' },
  { label: 'Start of This Week', value: 'thisWeek' },
  { label: 'Start of Last Week', value: 'lastWeek' },
  { label: 'Start of This Month', value: 'thisMonth' },
  { label: 'Start of Last Month', value: 'previous1Month' },
  { label: 'Custom', value: 'custom' },
]

/** Wire params for the endpoint. `periodHours` is deliberately absent: it is a
 *  client-side window language that `resolveWindow` has already turned into
 *  from/to, and `buildQuery` ships every key it is handed. */
const wireParams = (
  parameters: ReeferSpeedGraphParams,
  from: DateTime,
  to: DateTime
): ReeferSpeedGraphParams & { assetIds?: string; to?: string } => ({
  companyId: parameters.companyId,
  assetIds: parameters.assetId,
  date: 'custom',
  from: toWallClock(from),
  to: toWallClock(to),
  ...dataSourceWireParams(parameters),
})

const fetchSamples = async (
  api: ApiService,
  parameters: ReeferSpeedGraphParams,
  from: DateTime,
  to: DateTime
): Promise<ApiResponse<ReeferSpeedSample[]>> => {
  const query = buildQuery(wireParams(parameters, from, to))
  return api.get<ReeferSpeedSample[]>({
    path: `${PATH}?${query}&skip=0&take=${SAMPLE_LIMIT}`,
    origin: 'ReeferSpeedGraph',
  })
}

export const load = async (api: ApiService, parameters: ReeferSpeedGraphParams) => {
  const { from, to } = resolveWindow(parameters, getUserTimezone())
  const response = await fetchSamples(api, parameters, from, to)

  if (response.error) {
    return { code: InsightStatusCode.ERROR, message: response.message }
  }
  if (!response.data?.length) {
    return { code: InsightStatusCode.NO_DATA }
  }
  return response.data
}

const ReeferSpeedGraph: InsightDefinition<ReeferSpeedGraphParams, ReeferSpeedSample[]> = {
  active: true,
  slug: 'speed/reefer-speed-graph',
  category: 'speed',
  name: 'Speed Graph',
  description: 'Display reefer speed level (%) over time for an asset',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions.map((o) => ({ ...o, isDefault: o.value === 'today' })),
      },
      {
        id: 'from',
        label: 'Date',
        control: 'DateSelect',
        dependsOn: 'date',
        dependsOnValue: 'custom',
        showTime: true,
      },
      {
        id: 'periodHours',
        control: 'CustomSingleSelect',
        options: PERIOD_OPTIONS.map((o) => ({ ...o, isDefault: o.value === '24' })),
      },
    ],

    isValid: (configuration: ReeferSpeedGraphParams) => {
      if (configuration?.companyId && configuration.assetId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load,

  csv: async (api: ApiService, parameters: ReeferSpeedGraphParams, title: string) => {
    const { from, to } = resolveWindow(parameters, getUserTimezone())
    await downloadCsv(api, PATH, wireParams(parameters, from, to), title)
  },

  Display: ({ status, data, configuration }) => {
    const params = configuration.parameters as ReeferSpeedGraphParams
    // Memoized so `from` keeps a stable identity: useHistoryNavigation keys its
    // baseline off from.toMillis() and would otherwise reset on every render.
    const { from, hours } = useMemo(() => resolveWindow(params, getUserTimezone()), [params])
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    const speedUnit = units === 'Metric' ? 'kph' : 'mph'

    const reload = useCallback(
      async (api: ApiService, window: HistoryWindow): Promise<ReeferSpeedSample[] | undefined> => {
        const response = await fetchSamples(api, params, window.from, window.to)
        return response.data ?? undefined
      },
      [params]
    )

    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    // Render whenever we have data, including during a LOADING refresh — keeps the
    // chart mounted across status transitions so it doesn't blank on re-render.
    if (!data || isInsightStatus(data)) {
      return null
    }
    return (
      <section className="h-full w-full">
        <ReeferSpeedChart
          data={data}
          from={from}
          unit={speedUnit}
          periodHours={hours}
          reload={reload}
          externalLoading={status.code === InsightStatusCode.LOADING}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as ReeferSpeedGraphParams

    useEffect(() => {
      if (!api || !params.assetId) {
        return
      }
      const fn = async () => {
        const response = await api.get<Asset[]>({
          path: `/fresh/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false`,
          origin: 'ReeferSpeedGraph#caption',
        })
        if (response.data?.length) {
          setAsset(response.data[0])
        } else {
          setAsset({ id: Number(params.assetId) } as Asset)
        }
      }
      fn()
    }, [api, params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    // Named options are resolved against this insight's own list — the shared
    // helper only knows CustomSingleSelect's DateOptions, which has no
    // 'last24Hours' entry and would silently drop the Date field.
    const namedLabel =
      params.date && params.date !== 'custom' ? DateOptions.find((o) => o.value === params.date) : undefined
    if (namedLabel) {
      fields['Date'] = namedLabel.label
    } else {
      addDateToCaptionFields({ ...params, fields, finite: false })
    }
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data || isInsightStatus(data)) {
      return <div>No data found</div>
    }
    const readings = data.length
    return (
      <div>
        {readings} reading{readings === 1 ? '' : 's'}
      </div>
    )
  },
}

export default ReeferSpeedGraph
